<template>
	<v-btn ref="button"
		   v-bind="$attrs"
		   v-on="$listeners"
		   @click="tooltipVisible = false"
		   :dark="dark"
		   :to="to"
		   :light="light"
		   :color="color"
		   :outlined="outlined"
		   :height="buttonHeight"
		   :width="width"
		   :min-width="minWidth"
		   class="rmp-btn"
		   :disabled="disabled"
		   :loading="loading"
		   :plain="plain"
		   :block="block"
		   :href="href"
		   :text="text"
		   :elevation="elevation"
		   :small="small"
		   :icon="!!icon"
		   :class="[withoutPadding ? 'px-0' : buttonPadding]">
		<slot name="prepend">
			<component v-if="prependIcon" :is="iconComponent(prependIcon)"
					   v-bind="iconProps(prependIcon)" :class="iconClass ?? 'mr-2'">
				{{ iconText(prependIcon) }}
			</component>
		</slot>
		<slot>
			<component v-if="typeof icon === 'string' && icon" :is="iconComponent(icon)"
					   v-bind="iconProps(icon)" :class="iconClass">
				{{ iconText(icon) }}
			</component>
		</slot>
		<slot name="append">
			<component v-if="appendIcon" :is="iconComponent(appendIcon)"
					   v-bind="iconProps(appendIcon)" :class="iconClass ?? 'ml-2'">
				{{ iconText(appendIcon) }}
			</component>
		</slot>
		<v-tooltip v-if="tooltip && button && !disabled && !loading"
				   v-model="tooltipVisible"
				   :activator="button" bottom color="white" content-class="rmp-tooltip">
			<rmp-text tag="span" type="body-medium">{{ tooltip }}</rmp-text>
		</v-tooltip>
		<template v-if="slots.loader" #loader>
			<slot name="loader"></slot>
		</template>
	</v-btn>
</template>

<script setup lang="ts">
import { computed, ref, useSlots, watch } from "vue";
import RmpIcon from "@rmp/core/components/icon/RmpIcon.vue";
import RmpText from "@rmp/core/components/typography/RmpText.vue";

const slots = useSlots();
const button = ref<{ $el: HTMLElement } | null>(null);
const tooltipVisible = ref(false);

interface RmpBtnProps {
	dark?: boolean;
	light?: boolean;
	to?: Object;
	disabled?: boolean;
	outlined?: boolean;
	loading?: boolean;
	color?: string;
	dense?: boolean;
	plain?: boolean;
	block?: boolean;
	text?: boolean;
	elevation?: string | number;
	small?: boolean;
	icon?: boolean | string;
	prependIcon?: string;
	appendIcon?: string;
	iconSize?: string | number;
	/** Replaces the default icon spacing; an empty string removes it. */
	iconClass?: string | Record<string, unknown> | unknown[];
	tooltip?: string;
	withoutPadding?: boolean;
	height?: string | number;
	width?: string | number;
	minWidth?: string | number;
	href?: string;
	padding?: string;
	noMargin?: boolean;
}

const props = withDefaults(defineProps<RmpBtnProps>(), {
	dark: false,
	light: true,
	disabled: false,
	outlined: false,
	loading: false,
	color: "primary",
	dense: false,
	plain: false,
	block: false,
	text: false,
	elevation: 0,
	small: false,
	icon: false,
	iconSize: 20,
	withoutPadding: false,
	href: undefined
});

watch(() => props.disabled || props.loading || !props.tooltip, unavailable => {
	if(unavailable) tooltipVisible.value = false;
});

const buttonHeight = computed(() => props.height ?? (props.icon ? "auto" : 40));

const buttonPadding = computed(() => {
	return props.padding ?? (slots.prepend || props.prependIcon ? "pr-4 pl-3" : "px-4");
});

const svgIconSize = computed(() => Number.isNaN(Number(props.iconSize)) ? props.iconSize : `${Number(props.iconSize)}px`);

const iconComponent = (name: string) => name.startsWith("mdi-") ? "v-icon" : RmpIcon;
const iconProps = (name: string) => name.startsWith("mdi-")
	? { size: props.iconSize }
	: { src: name, size: svgIconSize.value, color: "inherit" };
const iconText = (name: string) => name.startsWith("mdi-") ? name : "";
</script>
